import { PageContainer } from '../layout/PageContainer';

export function TermsPage() {
  return (
    <PageContainer>
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold text-white mb-2">Terms of Service</h1>
        <p className="text-slate-400 mb-8">Last updated: February 2026</p>

        <div className="prose prose-invert prose-slate max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              1. Agreement to Terms
            </h2>
            <p className="text-slate-300 leading-relaxed">
              By accessing or using After5PM (&quot;the Service&quot;), you
              agree to be bound by these Terms of Service. If you disagree with
              any part of these terms, you may not access the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              2. Description of Service
            </h2>
            <p className="text-slate-300">
              After5PM is an online gaming platform providing browser-based
              casual games, leaderboards, and optional premium features. The
              Service is provided &quot;as is&quot; and may change over time.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              3. Account Registration
            </h2>
            <ul className="list-disc pl-6 text-slate-300 space-y-2">
              <li>
                You must provide accurate and complete registration information
              </li>
              <li>You are responsible for maintaining your account security</li>
              <li>You must notify us immediately of any unauthorized access</li>
              <li>
                You may not share your account or use another person&apos;s
                account
              </li>
              <li>
                We may suspend or terminate accounts that violate these terms
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              4. User Conduct
            </h2>
            <p className="text-slate-300 mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 text-slate-300 space-y-2">
              <li>Use cheats, exploits, automation software, or hacks</li>
              <li>Manipulate leaderboards or submit fraudulent scores</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Use offensive or inappropriate usernames</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Impersonate others or misrepresent your affiliation</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              5. Premium Subscription
            </h2>

            <h3 className="text-xl font-medium text-white mt-6 mb-3">
              Billing
            </h3>
            <ul className="list-disc pl-6 text-slate-300 space-y-2">
              <li>Premium subscriptions are billed monthly or annually</li>
              <li>Payments are processed by Stripe</li>
              <li>Subscriptions auto-renew unless cancelled</li>
              <li>You can cancel anytime; access continues until period end</li>
            </ul>

            <h3 className="text-xl font-medium text-white mt-6 mb-3">
              Refunds
            </h3>
            <p className="text-slate-300">
              Due to the digital nature of our service, refunds are generally
              not provided. Exceptions may be made at our discretion for
              technical issues preventing access.
            </p>

            <h3 className="text-xl font-medium text-white mt-6 mb-3">
              Price Changes
            </h3>
            <p className="text-slate-300">
              We may change subscription prices with 30 days notice. Existing
              subscriptions continue at their current rate until renewal.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              6. Intellectual Property
            </h2>
            <p className="text-slate-300 mb-4">
              The Service and its original content, features, and functionality
              are owned by After5PM and are protected by copyright, trademark,
              and other laws.
            </p>
            <ul className="list-disc pl-6 text-slate-300 space-y-2">
              <li>
                You may not copy, modify, or distribute our content without
                permission
              </li>
              <li>
                Game designs and mechanics may be inspired by classic games in
                the public domain
              </li>
              <li>
                User-generated content (usernames, scores) may be displayed
                publicly
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              7. Disclaimers
            </h2>
            <p className="text-slate-300 mb-4">
              THE SERVICE IS PROVIDED &quot;AS IS&quot; WITHOUT WARRANTIES OF
              ANY KIND, EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT:
            </p>
            <ul className="list-disc pl-6 text-slate-300 space-y-2">
              <li>The Service will be uninterrupted or error-free</li>
              <li>Defects will be corrected</li>
              <li>The Service is free of viruses or harmful components</li>
              <li>
                Results from using the Service will meet your expectations
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              8. Limitation of Liability
            </h2>
            <p className="text-slate-300">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, AFTER5PM SHALL NOT BE
              LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
              PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL,
              RESULTING FROM YOUR USE OF THE SERVICE.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              9. Indemnification
            </h2>
            <p className="text-slate-300">
              You agree to indemnify and hold harmless After5PM from any claims,
              damages, or expenses arising from your use of the Service or
              violation of these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              10. Termination
            </h2>
            <p className="text-slate-300">
              We may terminate or suspend your account immediately, without
              notice, for conduct that we believe violates these Terms or is
              harmful to other users, us, or third parties. Upon termination,
              your right to use the Service ceases.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              11. Governing Law
            </h2>
            <p className="text-slate-300">
              These Terms shall be governed by the laws of the State of Arizona,
              United States, without regard to conflict of law provisions. Any
              disputes shall be resolved in the courts of Arizona.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              12. Changes to Terms
            </h2>
            <p className="text-slate-300">
              We reserve the right to modify these Terms at any time. We will
              notify users of significant changes. Continued use of the Service
              after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              13. Contact
            </h2>
            <p className="text-slate-300">
              For questions about these Terms, contact us at:{' '}
              <a
                href="mailto:legal@after5pm.fun"
                className="text-purple-400 hover:text-purple-300"
              >
                legal@after5pm.fun
              </a>
            </p>
          </section>
        </div>
      </div>
    </PageContainer>
  );
}
